/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import { graphql } from 'gatsby';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';

import Layout from '../../Components/Core/Layout';
import SEO from '../../Components/Core/Seo';
import Container from '../../Components/Core/Container';
import BreadCrumbs from '../../Components/Custom/BreadCrumbs';
import BackToTop from '../../Components/Core/BackToTop';
import PageBuilder from '../../Components/Core/PageBuilder';
import VideoPopularSection from '../../Components/Custom/Video/VideoPopularSection';
import FAQSections from '../../Components/Custom/FAQ/FAQSections';
import VideoTutorialVideoPlayer from '../../Components/Custom/Video/VideoTutorialVideoPlayer';
import ProTip from '../../Components/Custom/ProTip';
import Feedback from '../../Components/Custom/Feedback';

import { Heading } from './styles';

const pageTemplate = {
  'popular-videos': VideoPopularSection,
  'related-faqs': FAQSections,
  'video-item': VideoTutorialVideoPlayer,
  'pro-tip': ProTip,
  'feedback-item': Feedback,
};

const VideoTutorialsLandingPage = ({
  data: {
    contentfulVideoTutorialPage: {
      pageTitle,
      sections,
      languageToggle,
      promoCode,
      customFooter,
      customHeader,
      internal: { type },
      pageSlug,
    },
  },
  location,
}) => {
  const intl = useIntl();

  return (
    <Layout
      location={location}
      header={customHeader}
      footer={customFooter}
      languageToggle={languageToggle}
      promoCode={promoCode}
      contentType={type}
    >
      <BreadCrumbs
        className="end-chevron"
        crumbs={[
          {
            label: `${intl.formatMessage({ id: 'learningHub' })}`,
            slug: '/learning-hub',
            active: true,
          },
          {
            label: `${intl.formatMessage({ id: 'VideoTutorialsCategory' })}`,
            slug: '/learning-hub/video-tutorials',
            active: true,
          },
        ]}
      />
      <Container className="video-tutorials">
        <Heading>{pageTitle}</Heading>
      </Container>
      <PageBuilder
        sections={sections}
        template={pageTemplate}
        pageSlug={pageSlug}
      />
      <BackToTop />
    </Layout>
  );
};

export default VideoTutorialsLandingPage;

VideoTutorialsLandingPage.propTypes = {
  location: PropTypes.shape().isRequired,
  data: PropTypes.shape().isRequired,
};

export const query = graphql`
  query VideoTutorialsLandingPageQuery(
    $locale: String = "en-CA"
    $contentful_id: String!
  ) {
    contentfulVideoTutorialPage(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $locale }
    ) {
      id
      contentful_id
      node_locale
      pageTitle
      metaDescription
      pageSlug
      languageToggle
      displayInSiteMap
      promoCode
      internal {
        type
      }
      videoLongDescription {
        raw
      }
      sections {
        ...video_item
        ...pro_tip
        ...faq_popular_question
        ...feedback_item
        ...popular_video
      }
      ...global_video_tutorial_header
      ...global_video_tutorial_footer
    }
  }
`;

export const Head = ({ location, data, pageContext }) => {
  const videoItem = data.contentfulVideoTutorialPage.sections.find(
    vid => vid.internal.type === 'ContentfulVideoItem',
  );

  const minutes = Math.round(videoItem.videoDuration / 60);
  const seconds = videoItem.videoDuration % 60;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: `${data.contentfulVideoTutorialPage.pageTitle}`,
    description: `${documentToPlainTextString(
      JSON.parse(videoItem.videoDescription.raw),
    )}`,
    thumbnailUrl: `https://img.youtube.com/vi/${videoItem.videoId}/sddefault.jpg`,
    uploadDate: `${videoItem.videoDate}`,
    duration: `PT${minutes}M${seconds}S`,
    publisher: {
      '@type': 'Organization',
      name: 'PC Financial',
      logo: {
        '@type': 'ImageObject',
        url: `https://www.pcfinancial.ca${data.contentfulVideoTutorialPage.customHeader.brandLogoDam.asset.localImage.publicURL}`,
        width: 120,
        height: 48,
      },
    },
    contentUrl: `https://www.youtube.com/watch?v=${videoItem.videoId}`,
    embedUrl: `https://www.pcfinancial.ca${location.pathname}`,
  };

  return (
    <SEO
      data={data.contentfulVideoTutorialPage}
      pageContext={pageContext}
      location={location}
    >
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </SEO>
  );
};
